import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, IdentificationBadge, ClockCounterClockwise, Sparkle, ArrowClockwise, Star, User, Buildings, CreditCard, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import "@/components/pvc-card.css";

// ------- Helpers -------
const PAPER_OPTIONS = [
  { id: "CR80",    label: "CR80 PVC (85.6×54mm)", layout: "single" },
  { id: "A4",      label: "A4 Sheet (10 cards)",  layout: "8-per-A4" },
  { id: "4x6",     label: "4×6 Sheet (2 cards)",  layout: "2-per-4x6" },
  { id: "thermal", label: "Thermal Receipt",       layout: "single" },
];

const RIBBON_OPTIONS = [
  { id: "full", label: "Full Panel (Colour)" },
  { id: "half", label: "Half Panel" },
  { id: "mono", label: "Black / Monochrome" },
];

const SIDES_OPTIONS = [
  { id: "front_back", label: "Front + Back" },
  { id: "front",      label: "Front Only" },
  { id: "back",       label: "Back Only" },
];

function today() {
  return new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function autoFillFrom(customer, template, existing = {}) {
  const src = customer || {};
  const full_address = [src.house_no, src.street, src.address, src.city, src.taluka, src.district, src.state, src.pin]
    .filter(Boolean).join(", ");
  const seed = { ...src, full_address, today: today() };
  const pull = (fields) => {
    const out = { ...existing };
    for (const f of fields) {
      if (out[f.key]) continue; // keep any manual edit
      if (f.source && seed[f.source]) out[f.key] = seed[f.source];
    }
    return out;
  };
  return {
    front: pull(template.front || []),
    back:  pull(template.back  || []),
  };
}

// ------- Print helpers -------
function openPrintWindow(html) {
  const w = window.open("", "_blank", "width=900,height=1200");
  if (!w) { toast.error("Popup blocked — please allow popups to print."); return null; }
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { try { w.print(); } catch {} }, 350);
  return w;
}

function svgAtSize(text, size) {
  // Serialize a QRCodeSVG using a hidden mount
  // Fallback: use qrserver.com? No — offline.
  // We'll render via a temporary React tree in the sheet HTML using a base64 SVG from qrcode-generator would be heavy.
  // Simpler: mark [[QR:text]] and let the sheet HTML skip QR (print CSS uses embedded SVG from live preview).
  return null;
}

export default function PrintCenter() {
  const [tab, setTab] = useState("designer"); // designer | history | templates
  const [templates, setTemplates] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [autoRoutes, setAutoRoutes] = useState({});
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("_none");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [templateId, setTemplateId] = useState("aadhaar_pvc");
  const [fieldsFront, setFieldsFront] = useState({});
  const [fieldsBack,  setFieldsBack]  = useState({});

  const [paper, setPaper]     = useState("CR80");
  const [ribbon, setRibbon]   = useState("full");
  const [sides, setSides]     = useState("front_back");
  const [copies, setCopies]   = useState(1);
  const [printerId, setPrinterId] = useState("");
  const [createBill, setCreateBill] = useState(false);
  const [billAmount, setBillAmount] = useState(50);

  const [history, setHistory] = useState([]);
  const [historyQ, setHistoryQ] = useState("");

  // Load templates + customers on mount
  useEffect(() => {
    api.get("/pvc/templates").then((r) => {
      setTemplates(r.data.templates || []);
      setPrinters(r.data.printers || []);
      setAutoRoutes(r.data.auto_routes || {});
    }).catch(() => toast.error("Could not load templates"));
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  // When template changes, recompute auto-printer & prefill fields from selected customer
  const template = useMemo(() => templates.find((t) => t.id === templateId), [templateId, templates]);

  useEffect(() => {
    if (!template) return;
    setPrinterId(autoRoutes[templateId] || "");
    const filled = autoFillFrom(selectedCustomer, template, {});
    setFieldsFront(filled.front);
    setFieldsBack(filled.back);
  }, [templateId, template, selectedCustomer, autoRoutes]);

  const pickCustomer = async (cid) => {
    setCustomerId(cid);
    if (cid === "_none") { setSelectedCustomer(null); return; }
    try {
      const { data } = await api.get(`/customers/${cid}`);
      setSelectedCustomer(data);
    } catch { setSelectedCustomer(null); }
  };

  const setFieldF = (k, v) => setFieldsFront((f) => ({ ...f, [k]: v }));
  const setFieldB = (k, v) => setFieldsBack((f) => ({ ...f, [k]: v }));

  const loadHistory = async () => {
    const { data } = await api.get("/pvc/history", { params: { q: historyQ || undefined } });
    setHistory(data);
  };
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab, historyQ]);

  const deleteHistoryItem = async (id) => {
    if (!window.confirm("Delete this print record?")) return;
    try {
      await api.delete(`/pvc/history/${id}`);
      loadHistory();
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reprintHistoryItem = (h) => {
    setTemplateId(h.template_id);
    setFieldsFront(h.fields_front || {});
    setFieldsBack(h.fields_back || {});
    setPaper(h.paper || "CR80");
    setRibbon(h.ribbon || "full");
    setSides(h.sides || "front_back");
    setPrinterId(h.printer_id || "");
    setCopies(h.copies || 1);
    setCustomerId(h.customer_id || "_none");
    if (h.customer_id) pickCustomer(h.customer_id);
    setTab("designer");
    toast.success("Loaded — click Print to reprint.");
  };

  const savePrintRecord = async () => {
    try {
      await api.post("/pvc/print", {
        template_id: templateId,
        customer_id: customerId === "_none" ? "" : customerId,
        fields_front: fieldsFront,
        fields_back: fieldsBack,
        paper, ribbon, sides,
        printer_id: printerId,
        copies: Number(copies) || 1,
        create_bill: createBill,
        bill_amount: createBill ? Number(billAmount) || 0 : 0,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save print record");
      throw e;
    }
  };

  // Print — clone the live-preview HTML into a new window with print CSS
  const previewRef = useRef(null);
  const cssRef = useRef(null);
  useEffect(() => {
    if (cssRef.current) return;
    const link = Array.from(document.styleSheets).find((s) => (s.href || "").includes("pvc-card"));
    if (link && link.href) cssRef.current = link.href;
  }, []);

  const buildSheetHtml = () => {
    if (!previewRef.current) return "";
    const inline = Array.from(document.styleSheets)
      .map((s) => { try { return Array.from(s.cssRules).map((r) => r.cssText).join("\n"); } catch { return ""; } })
      .filter(Boolean).join("\n");
    const frontHtml = previewRef.current.querySelector("[data-side='front']")?.outerHTML || "";
    const backHtml  = previewRef.current.querySelector("[data-side='back']")?.outerHTML  || "";
    const paperOpt = PAPER_OPTIONS.find((p) => p.id === paper) || PAPER_OPTIONS[0];
    const wrapCls  = paper === "A4" ? "sheet-a4" : paper === "4x6" ? "sheet-4x6" : "sheet-single";

    const showFront = sides === "front" || sides === "front_back";
    const showBack  = sides === "back"  || sides === "front_back";

    // Compose the sheet children
    let cardsHtml = "";
    if (paper === "A4") {
      // 10 slots — alternate front/back if front_back, else fill with the chosen side
      const total = 10;
      for (let i = 0; i < total; i++) {
        if (sides === "front_back") cardsHtml += (i % 2 === 0 ? frontHtml : backHtml);
        else if (sides === "front")  cardsHtml += frontHtml;
        else                          cardsHtml += backHtml;
      }
    } else if (paper === "4x6") {
      if (sides === "front_back") { cardsHtml = frontHtml + backHtml; }
      else if (sides === "front") { cardsHtml = frontHtml + frontHtml; }
      else                         { cardsHtml = backHtml  + backHtml;  }
    } else {
      // Single or thermal — one card per copy, N pages using page-break
      const one = sides === "front_back" ? (frontHtml + "<div style='page-break-after:always'></div>" + backHtml)
                : sides === "front" ? frontHtml : backHtml;
      cardsHtml = Array.from({ length: Math.max(1, Number(copies) || 1) }).map(() => one).join("<div style='page-break-after:always'></div>");
    }

    return `<!doctype html><html><head><meta charset="utf-8"/><title>PVC Print — ${template?.name || ""}</title>
      <style>${inline}
      @page { size: ${paper === "A4" ? "A4" : paper === "4x6" ? "152.4mm 101.6mm" : "85.6mm 54mm"}; margin: 0; }
      body { margin: 0; background: white; }
      .pvc-print-sheet { visibility: visible !important; }
      .pvc-print-sheet .pvc-card { transform: none !important; margin: 0 !important; }
      </style></head><body>
      <div class="pvc-print-sheet ${wrapCls}">${cardsHtml}</div>
      </body></html>`;
  };

  const doPrint = async () => {
    const html = buildSheetHtml();
    if (!html) { toast.error("Nothing to print"); return; }
    try {
      await savePrintRecord();
    } catch { return; }
    openPrintWindow(html);
    toast.success("Sent to your printer — check the OS dialog");
  };

  // Grouped templates for the picker
  const groupedTemplates = useMemo(() => {
    const groups = {};
    for (const t of templates) (groups[t.group || "Other"] = groups[t.group || "Other"] || []).push(t);
    return groups;
  }, [templates]);

  return (
    <div className="max-w-[1500px] mx-auto p-6" data-testid="print-center-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <IdentificationBadge size={14} weight="fill" className="text-orange-500" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500">Smart PVC & Identity Print Center</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Print Center</h1>
          <p className="text-sm text-slate-500 mt-1">Design & print CR80 PVC cards · Aadhaar / PAN / Voter / Employee ID · with live front & back preview.</p>
        </div>

        <div className="flex gap-2" data-testid="pc-tabs">
          <TabPill active={tab === "designer"} onClick={() => setTab("designer")} testid="pc-tab-designer" icon={CreditCard}>Card Designer</TabPill>
          <TabPill active={tab === "history"} onClick={() => setTab("history")} testid="pc-tab-history" icon={ClockCounterClockwise}>Print History</TabPill>
          <TabPill active={tab === "templates"} onClick={() => setTab("templates")} testid="pc-tab-templates" icon={Star}>Templates</TabPill>
        </div>
      </div>

      {tab === "designer" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT — data */}
          <div className="lg:col-span-4 space-y-4">
            <Card className="p-4 space-y-3 bg-white border-slate-200" data-testid="pc-customer-card">
              <FieldTitle icon={User}>Customer</FieldTitle>
              <Select value={customerId} onValueChange={pickCustomer}>
                <SelectTrigger data-testid="pc-customer-select"><SelectValue placeholder="Optional — none" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="_none">— None (manual entry) —</SelectItem>
                  {customers.slice(0, 300).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.customer_no} · {c.name} · {c.mobile}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCustomer && (
                <div className="text-xs text-slate-500">
                  Auto-filled from <span className="font-medium text-slate-900">{selectedCustomer.name}</span>. You can still edit any field below.
                </div>
              )}
            </Card>

            <Card className="p-4 space-y-3 bg-white border-slate-200" data-testid="pc-template-card">
              <FieldTitle icon={CreditCard}>Card Template</FieldTitle>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger data-testid="pc-template-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-96">
                  {Object.entries(groupedTemplates).map(([g, items]) => (
                    <div key={g}>
                      <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-orange-600 bg-slate-50">{g}</div>
                      {items.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </div>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 italic">
                Auto-routed to: <span className="font-medium text-slate-800">
                  {printers.find((p) => p.id === (autoRoutes[templateId] || printerId))?.label || "OS default"}
                </span>
              </div>
            </Card>

            {/* Front fields */}
            {template && (
              <Card className="p-4 space-y-3 bg-white border-slate-200" data-testid="pc-front-fields">
                <FieldTitle>Front Side</FieldTitle>
                {(template.front || []).filter((f) => f.type !== "photo" && f.type !== "qr").map((f) => (
                  <div key={f.key}>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">{f.label}</Label>
                    {f.type === "textarea" ? (
                      <textarea rows={2} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm mt-1" value={fieldsFront[f.key] || ""} onChange={(e) => setFieldF(f.key, e.target.value)} />
                    ) : (
                      <Input className="mt-1" value={fieldsFront[f.key] || ""} onChange={(e) => setFieldF(f.key, e.target.value)} data-testid={`pc-front-${f.key}`} />
                    )}
                  </div>
                ))}
                {/* Photo upload */}
                {(template.front || []).some((f) => f.type === "photo") && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">Photograph</Label>
                    <input type="file" accept="image/*" data-testid="pc-photo-input" className="text-xs mt-1 file:mr-2 file:py-1.5 file:px-2 file:rounded file:border-0 file:bg-orange-600 file:text-white"
                      onChange={(e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        if (file.size > 2_000_000) { toast.error("Max 2 MB photo"); return; }
                        const r = new FileReader();
                        r.onload = () => setFieldF("photo", r.result);
                        r.readAsDataURL(file);
                      }} />
                    {fieldsFront.photo && (
                      <div className="mt-2 flex items-center gap-2">
                        <img src={fieldsFront.photo} alt="" className="w-10 h-12 rounded border object-cover" />
                        <button className="text-xs text-red-600" onClick={() => setFieldF("photo", "")}>Remove</button>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* Back fields */}
            {template && (template.back || []).length > 0 && (
              <Card className="p-4 space-y-3 bg-white border-slate-200" data-testid="pc-back-fields">
                <FieldTitle>Back Side</FieldTitle>
                {(template.back || []).filter((f) => f.type !== "photo" && f.type !== "qr").map((f) => (
                  <div key={f.key}>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">{f.label}</Label>
                    {f.type === "textarea" ? (
                      <textarea rows={2} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm mt-1" value={fieldsBack[f.key] || ""} onChange={(e) => setFieldB(f.key, e.target.value)} />
                    ) : (
                      <Input className="mt-1" value={fieldsBack[f.key] || ""} onChange={(e) => setFieldB(f.key, e.target.value)} data-testid={`pc-back-${f.key}`} />
                    )}
                  </div>
                ))}
              </Card>
            )}
          </div>

          {/* CENTER — live preview */}
          <div className="lg:col-span-5">
            <Card className="p-6 bg-slate-50 border-slate-200 pvc-preview" data-testid="pc-preview">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-4 flex items-center gap-2">
                <Sparkle size={11} weight="fill" className="text-orange-500" /> Live Preview · Actual CR80 size scaled 2.2×
              </div>
              <div ref={previewRef}>
                {template ? (
                  <>
                    <div className="mb-6">
                      <div className="text-[9px] uppercase tracking-[0.25em] text-slate-400 mb-1.5">Front</div>
                      <CardSide side="front" template={template} fields={fieldsFront} />
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.25em] text-slate-400 mb-1.5">Back</div>
                      <CardSide side="back" template={template} fields={fieldsBack} />
                    </div>
                  </>
                ) : (
                  <div className="text-slate-400 text-sm py-20 text-center">Pick a template on the left</div>
                )}
              </div>
            </Card>
          </div>

          {/* RIGHT — print options */}
          <div className="lg:col-span-3 space-y-4">
            <Card className="p-4 bg-white border-slate-200 space-y-3" data-testid="pc-print-options">
              <FieldTitle icon={Printer}>Print Options</FieldTitle>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Paper</Label>
                <Select value={paper} onValueChange={setPaper}>
                  <SelectTrigger className="mt-1" data-testid="pc-paper-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAPER_OPTIONS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Ribbon</Label>
                <Select value={ribbon} onValueChange={setRibbon}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{RIBBON_OPTIONS.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Sides</Label>
                <Select value={sides} onValueChange={setSides}>
                  <SelectTrigger className="mt-1" data-testid="pc-sides-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SIDES_OPTIONS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Copies</Label>
                <Input type="number" min={1} max={100} value={copies} onChange={(e) => setCopies(e.target.value)} className="mt-1" data-testid="pc-copies-input" />
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Printer</Label>
                <Select value={printerId} onValueChange={setPrinterId}>
                  <SelectTrigger className="mt-1" data-testid="pc-printer-select"><SelectValue placeholder="Auto-detect" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_auto">Auto (recommended)</SelectItem>
                    {printers.map((p) => <SelectItem key={p.id} value={p.id}>{p.icon} {p.label} · {p.kind}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-slate-400 mt-1 italic">
                  The browser will open your OS printer dialog — pick the correct physical printer there.
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-white border-slate-200 space-y-3" data-testid="pc-bill-card">
              <FieldTitle icon={Buildings}>Auto-bill</FieldTitle>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" data-testid="pc-bill-toggle" checked={createBill} onChange={(e) => setCreateBill(e.target.checked)} className="w-4 h-4" />
                Create a POS bill for this print
              </label>
              {createBill && (
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500">Amount (₹)</Label>
                  <Input type="number" min={0} value={billAmount} onChange={(e) => setBillAmount(e.target.value)} className="mt-1" data-testid="pc-bill-amount" />
                  <div className="text-[10px] text-slate-500 mt-1">Requires a customer to be selected.</div>
                </div>
              )}
            </Card>

            <Button onClick={doPrint} className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white gap-2 text-sm font-semibold" data-testid="pc-print-btn">
              <Printer size={16} weight="bold" /> Print &amp; Save to History
            </Button>
          </div>
        </div>
      )}

      {tab === "history" && (
        <Card className="p-4 bg-white border-slate-200" data-testid="pc-history-panel">
          <div className="flex items-center gap-2 mb-3">
            <MagnifyingGlass size={14} className="text-slate-400" />
            <Input placeholder="Search by customer, template, printer…" value={historyQ} onChange={(e) => setHistoryQ(e.target.value)} className="max-w-sm" data-testid="pc-history-search" />
            <Button variant="outline" onClick={loadHistory} className="gap-1.5 h-9"><ArrowClockwise size={13} /> Refresh</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
                  <th className="text-left py-2">Printed At</th>
                  <th className="text-left py-2">Template</th>
                  <th className="text-left py-2">Customer</th>
                  <th className="text-left py-2">Printer</th>
                  <th className="text-left py-2">Paper</th>
                  <th className="text-left py-2">Copies</th>
                  <th className="text-left py-2">Operator</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && <tr><td colSpan={8} className="py-16 text-center text-slate-400">No print history yet.</td></tr>}
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`pc-history-row-${h.id}`}>
                    <td className="py-2 text-xs">{h.printed_at ? new Date(h.printed_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                    <td className="py-2"><Badge variant="outline" className="text-[10px]">{h.template_name}</Badge></td>
                    <td className="py-2 text-slate-800">{h.customer_name || <span className="text-slate-400">—</span>}</td>
                    <td className="py-2 text-slate-600 text-xs">{printers.find((p) => p.id === h.printer_id)?.label || h.printer_id || "OS"}</td>
                    <td className="py-2 text-xs">{h.paper}</td>
                    <td className="py-2 text-xs">{h.copies}</td>
                    <td className="py-2 text-xs text-slate-600">{h.operator}</td>
                    <td className="py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => reprintHistoryItem(h)} className="text-xs gap-1 h-7" data-testid={`pc-reprint-${h.id}`}><ArrowClockwise size={11} /> Reprint</Button>
                      <Button size="icon" variant="ghost" onClick={() => deleteHistoryItem(h.id)} className="text-red-600 hover:bg-red-50 ml-1 h-7 w-7" data-testid={`pc-del-${h.id}`}><Trash size={12} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "templates" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pc-templates-grid">
          {templates.map((t) => (
            <Card key={t.id} className="p-4 bg-white border-slate-200 hover:border-orange-300 transition-colors cursor-pointer"
              onClick={() => { setTemplateId(t.id); setTab("designer"); }} data-testid={`pc-tpl-card-${t.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-md flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: t.bg }}>
                  {t.name[0]}
                </div>
                <div>
                  <div className="font-semibold text-slate-900 text-sm">{t.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{t.group}</div>
                </div>
              </div>
              <div className="text-xs text-slate-600">
                {(t.front || []).length + (t.back || []).length} fields · {(t.back || []).length ? "Front + Back" : "Front only"}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// -------- Card render helpers --------
function CardSide({ side, template, fields }) {
  const hasPhoto = (template[side] || []).some((f) => f.type === "photo");
  const nonSpecial = (template[side] || []).filter((f) => f.type !== "photo" && f.type !== "qr");
  const qrField = (template[side] || []).find((f) => f.type === "qr");
  const qrValue = qrField ? (fields[qrField.key] || fields.name || template.name) : "";
  const bandBg = template.accent || "#0f172a";
  const cardBg = template.bg || "#0f172a";
  const textOnBg = getTextColor(cardBg);
  const textOnBand = getTextColor(bandBg);

  return (
    <div className={`pvc-card ${hasPhoto ? "" : "no-photo"}`} data-side={side} style={{ background: cardBg, color: textOnBg }}>
      <div className="brand-band" style={{ background: bandBg, color: textOnBand }}>
        <span>{template.name}</span>
        <span style={{ opacity: 0.9 }}>DigitalSetu</span>
      </div>
      <div className="body">
        {hasPhoto && (
          <div className="photo-box">
            {fields.photo ? <img src={fields.photo} alt="" /> : <span>PHOTO</span>}
          </div>
        )}
        <div className="field-list">
          {nonSpecial.map((f, i) => (
            <div key={f.key} className={`field ${i === 0 ? "big" : ""}`}>
              <div className="k">{f.label}</div>
              <div className="v">{fields[f.key] || <span style={{ opacity: 0.5 }}>—</span>}</div>
            </div>
          ))}
        </div>
      </div>
      {qrField && (
        <div className="qr-slot">
          <QRCodeSVG value={String(qrValue || template.name)} size={44} level="M" bgColor="#ffffff" fgColor="#000000" />
        </div>
      )}
      <div className="footer-band" style={{ background: bandBg, color: textOnBand }}>{template.group}</div>
    </div>
  );
}

function TabPill({ active, onClick, children, icon: Icon, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-medium transition-colors ${active ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"}`}
    >
      {Icon && <Icon size={14} weight={active ? "fill" : "regular"} />} {children}
    </button>
  );
}

function FieldTitle({ children, icon: Icon }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-600 flex items-center gap-1.5">
      {Icon && <Icon size={11} weight="fill" className="text-orange-500" />} {children}
    </div>
  );
}

// Compute readable text color (white / near-black) against a hex background
function getTextColor(hex) {
  try {
    const h = hex.replace("#", "");
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 150 ? "#0f172a" : "#ffffff";
  } catch { return "#ffffff"; }
}
